import type { MatchSession } from '../game/match';

/**
 * A controller for the match (a DualSense over USB or Bluetooth, or anything with the standard
 * mapping). Polled every frame from navigator.getGamepads().
 *
 *   D-pad / left stick   move the highlight over the action grid or the shop; walk in the move phase
 *   Left stick           aim: direction is the angle, how far it is pushed is the power
 *   Cross (×) / R2       pick the highlighted thing; jump while walking; throw or shoot while aiming
 *   Circle (○)           back out of aiming; Done while walking
 *   Square (□)           skip the turn; sell one back in the shop
 *   Triangle (△)         next mole (while picking)
 *   Options              Ready in the shop; Done while walking
 *
 * Brawl (the live phase) has no turns, so the sticks split the work: the left stick walks, the
 * right stick aims, Cross jumps, R2 or Circle fires, Triangle cycles to the next weapon with
 * ammo, the D-pad moves the highlight over the weapon grid and Square picks the highlighted tile.
 *
 * Menus are driven through the DOM: anything with a `data-pad` attribute is a stop, the nearest
 * one in the pushed direction gets `.pad-focus`, and Cross clicks it. Square clicks the sibling
 * marked `data-pad-alt` (the shop's − button); Options clicks `data-pad-ready`.
 */
export class PadInput {
  private raf = 0;
  private prev: boolean[] = [];
  private navDir = '';
  private navNext = 0;
  private walkDir: -1 | 0 | 1 = 0;
  private seen = false;
  private disposed = false;

  constructor(private readonly session: MatchSession, private readonly root: HTMLElement, private readonly onConnect: () => void) {
    if (typeof navigator !== 'undefined' && 'getGamepads' in navigator) this.raf = requestAnimationFrame(this.loop);
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
  }

  private loop = (now: number): void => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.loop);
    let gp: Gamepad | null = null;
    for (const p of navigator.getGamepads()) if (p && p.connected && p.mapping === 'standard') { gp = p; break; }
    if (!gp) {
      for (const p of navigator.getGamepads()) if (p && p.connected) { gp = p; break; }
    }
    if (!gp) return;
    if (!this.seen) {
      this.seen = true;
      this.onConnect();
    }
    this.handle(gp, now);
  };

  private edge(gp: Gamepad, i: number): boolean {
    const now = gp.buttons[i]?.pressed ?? false;
    const was = this.prev[i] ?? false;
    this.prev[i] = now;
    return now && !was;
  }

  private handle(gp: Gamepad, now: number): void {
    const lx = gp.axes[0] ?? 0;
    const ly = gp.axes[1] ?? 0;
    const padX = gp.buttons[15]?.pressed ? 1 : gp.buttons[14]?.pressed ? -1 : 0;
    const padY = gp.buttons[13]?.pressed ? 1 : gp.buttons[12]?.pressed ? -1 : 0;
    const dirX = padX || (Math.abs(lx) > 0.5 ? Math.sign(lx) : 0);
    const dirY = padY || (Math.abs(ly) > 0.5 ? Math.sign(ly) : 0);
    const cross = this.edge(gp, 0);
    const circle = this.edge(gp, 1);
    const square = this.edge(gp, 2);
    const triangle = this.edge(gp, 3);
    const r2 = this.edge(gp, 7);
    const options = this.edge(gp, 9);
    const up = this.edge(gp, 12);
    const phase = this.session.phase;
    // In a brawl the left stick walks, so only the D-pad drives the weapon grid.
    const nav = phase === 'live' ? this.navPulse(padX, padY, now) : this.navPulse(dirX, dirY, now);

    if (phase === 'buy') {
      if (nav) this.moveFocus(nav.dx, nav.dy);
      if (cross) this.clickFocused();
      if (square) this.clickAlt();
      if (options) this.clickReady();
      return;
    }
    if (!this.session.myTurn) return;
    if (phase === 'live') {
      const dir = (Math.abs(lx) > 0.35 ? Math.sign(lx) : 0) as -1 | 0 | 1;
      if (dir !== this.walkDir) {
        this.walkDir = dir;
        this.session.move(dir);
      }
      if (cross) this.session.jump();
      const rx = gp.axes[2] ?? 0;
      const ry = gp.axes[3] ?? 0;
      const mag = Math.hypot(rx, ry);
      const dead = 0.22;
      if (mag > dead) {
        const angle = Math.atan2(-ry, Math.abs(rx));
        const power = Math.min(1, (mag - dead) / (1 - dead));
        this.session.padAim(angle, power, rx < 0 ? -1 : 1);
      }
      if (r2 || circle) this.session.padFire();
      if (triangle) this.session.cycleAction();
      if (nav) this.moveFocus(nav.dx, nav.dy);
      if (square) this.clickFocused();
      return;
    }
    switch (phase) {
      case 'select':
        if (nav) this.moveFocus(nav.dx, nav.dy);
        if (cross) this.clickFocused();
        if (square) this.session.skipTurn();
        if (triangle) this.session.nextUnit();
        break;
      case 'move': {
        const dir = dirX as -1 | 0 | 1;
        if (dir !== this.walkDir) {
          this.walkDir = dir;
          this.session.move(dir);
        }
        if (cross || up) this.session.jump();
        if (circle || options) this.session.stopMove();
        break;
      }
      case 'aim': {
        const mag = Math.hypot(lx, ly);
        const dead = 0.22;
        if (mag > dead) {
          const angle = Math.atan2(-ly, Math.abs(lx));
          const power = Math.min(1, (mag - dead) / (1 - dead));
          this.session.padAim(angle, power, lx < 0 ? -1 : 1);
        }
        if (cross || r2) this.session.padFire();
        if (circle) this.session.cancel();
        break;
      }
      default:
        break;
    }
    if (phase !== 'move' && this.walkDir !== 0) this.walkDir = 0;
  }

  /** One step per press, then repeats while held: 350 ms, then every 150 ms. */
  private navPulse(dx: number, dy: number, now: number): { dx: number; dy: number } | null {
    const key = dx || dy ? `${dx},${dy}` : '';
    if (key !== this.navDir) {
      this.navDir = key;
      this.navNext = now + 350;
      return key ? { dx, dy } : null;
    }
    if (key && now >= this.navNext) {
      this.navNext = now + 150;
      return { dx, dy };
    }
    return null;
  }

  // ---------- DOM focus ----------

  private stops(): HTMLElement[] {
    return [...this.root.querySelectorAll<HTMLElement>('[data-pad]')].filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
  }

  private focused(): HTMLElement | null {
    const el = this.root.querySelector<HTMLElement>('.pad-focus');
    return el && el.offsetParent !== null ? el : null;
  }

  private setFocus(el: HTMLElement): void {
    for (const old of this.root.querySelectorAll('.pad-focus')) old.classList.remove('pad-focus');
    el.classList.add('pad-focus');
    el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }

  /** The nearest stop in the pushed direction (a cone around it), else the first stop. */
  private moveFocus(dx: number, dy: number): void {
    const stops = this.stops();
    if (stops.length === 0) return;
    const cur = this.focused();
    if (!cur) {
      this.setFocus(stops[0]!);
      return;
    }
    const a = cur.getBoundingClientRect();
    const ax = a.left + a.width / 2;
    const ay = a.top + a.height / 2;
    let best: HTMLElement | null = null;
    let bestScore = Infinity;
    for (const el of stops) {
      if (el === cur) continue;
      const b = el.getBoundingClientRect();
      const vx = b.left + b.width / 2 - ax;
      const vy = b.top + b.height / 2 - ay;
      const along = vx * dx + vy * dy;
      if (along <= 4) continue;
      const across = Math.abs(vx * dy) + Math.abs(vy * dx);
      if (across > along * 1.6 + 24) continue;
      const score = along + across * 2.5;
      if (score < bestScore) {
        bestScore = score;
        best = el;
      }
    }
    if (best) this.setFocus(best);
  }

  private clickFocused(): void {
    const el = this.focused();
    if (el) el.click();
    else {
      const first = this.stops()[0];
      if (first) this.setFocus(first);
    }
  }

  private clickAlt(): void {
    const el = this.focused();
    const alt = el?.parentElement?.querySelector<HTMLElement>('[data-pad-alt]');
    alt?.click();
  }

  private clickReady(): void {
    this.root.querySelector<HTMLElement>('[data-pad-ready]')?.click();
  }
}
